/**
 * `/v1/{gameCode}/*` — 單一遊戲端點（2026-07 改版）
 *
 * ⚠️ **`/v1/gamecenter/*` 已停用**，改成每款遊戲各自的 URL：
 *   `/v1/{gameCode}/bets` / `/v1/{gameCode}/spin` / `/v1/{gameCode}/buy`。
 *   - 路徑段 `{gameCode}` 一律 **小寫**（= host gameId / SET_CONFIG 的 gameCode），
 *     本層統一 `toLowerCase()`，呼叫端傳大小寫皆可。
 *   - 遊戲已由路徑識別 → **body 不再帶 `game_code`**。
 *
 * baseURL 走 http.ts 預設（runtimeConfig `API_BASE`）。慣例見 `src/api/CLAUDE.md`。
 */
import { useHttp } from '../http'
import type {
  BetsResponse,
  BetsResult,
  BuyParams,
  FetchBetsParams,
  SpinParams,
  SpinResult,
  FetchVersionParams,
  VersionData,
} from './types'

/** gameCode → 路徑段（小寫）。 */
const seg = (gameCode: string): string =>
  String(gameCode ?? '')
    .trim()
    .toLowerCase()

const maps = {
  bets: (g: string) => `/v1/${seg(g)}/bets`,
  spin: (g: string) => `/v1/${seg(g)}/spin`,
  buy: (g: string) => `/v1/${seg(g)}/buy`,
  version: (g: string) => `/v1/${seg(g)}/version`,
} as const

/**
 * 取得當前帳戶幣別 + 投注檔位
 * GET `/v1/{gameCode}/bets`
 *
 * 回傳已 normalize 過：currency 強制 upper-case、amounts 保證為 array
 * 失敗（網路 / 業務錯）一律 throw ApiError，呼叫端用 try/catch 接
 */
export const fetchBets = async (params: FetchBetsParams): Promise<BetsResult> => {
  const data = await useHttp.get<BetsResponse>(maps.bets(params.gameCode))

  return {
    currency: String(data?.currency ?? '')
      .trim()
      .toUpperCase(),
    name: data?.name ?? '',
    defaultSize: typeof data?.defaultSize === 'number' ? data.defaultSize : 0,
    amounts: Array.isArray(data?.amounts) ? data.amounts : [],
  }
}

/**
 * 一般旋轉（開發用：local / dev 端 DevSpinPanel 打 seed 重現特定盤面）
 * POST `/v1/{gameCode}/spin`
 *
 * 帶 seed 時後端依該種子重現該局；不帶 seed 為一般隨機旋轉。
 * 失敗一律 throw ApiError。
 */
export const spin = async (params: SpinParams): Promise<SpinResult> => {
  const body: Record<string, unknown> = {
    bet_size: params.betSize,
    bet_level: params.betLevel,
    bet_ways: params.betWays,
  }
  if (params.seed) body.seed = params.seed
  return useHttp.post<SpinResult>(maps.spin(params.gameCode), body)
}

/**
 * 購買免費遊戲（Feature Buy）
 * POST `/v1/{gameCode}/buy`
 *
 * 扣款倍率由後端依該款規則決定（如 shbz 75x），前端只送注額參數。
 * 失敗一律 throw ApiError。
 */
export const buy = async (params: BuyParams): Promise<SpinResult> =>
  useHttp.post<SpinResult>(maps.buy(params.gameCode), {
    bet_size: params.betSize,
    bet_level: params.betLevel,
    bet_ways: params.betWays,
  })

/**
 * 取該款後端 runner 版本（VersionBadge 的 `server` 值）
 * GET `/v1/{gameCode}/version`
 *
 * ⚠️ **端點要 token**（2026-09-07 後端改版；呼叫端 useVersionInfo 已等 `authReady` 才打）。
 * 🛑 **`skipAuthRecovery: true` 不可省**：這支是 GET＝冪等，`retryOn401` 預設 true →
 *    401 會**先 await 重新登入、再重發**。版本角標是純資訊，就算真的 token 失效也不該
 *    由它觸發重新登入（AUTH_INVALID 流程自有主流程扛）。
 *
 * 🛑 **路徑段必須小寫**：實測 `/v1/TTQJ/version` 回 app 層 404，`seg()` 已統一轉小寫。
 *
 * 取不到 → 回 `''`，由呼叫端決定怎麼顯示（VersionBadge 顯示 '-'）。
 * **不 throw**：版本角標是純資訊，任何失敗都不該影響進遊戲流程。
 */
export const fetchGameVersion = async (params: FetchVersionParams): Promise<string> => {
  try {
    const data = await useHttp.get<VersionData>(maps.version(params.gameCode), undefined, {
      skipAuthRecovery: true,
    })
    return String(data?.version ?? '').trim()
  } catch {
    return ''
  }
}
